import { MandatoryItemDataInvestmentTypeEnum, type ItemData } from "@eu.mdotm.api/smart-workspaces";
import type { IconName } from "@mdotm/shadcn/legacy/components";
import type { SpawnResult } from "@mdotm/shadcn/legacy/react-extensions";
import { typedObjectValues } from "@mdotm/shadcn/legacy/utils";
import { portfolioLabRepository } from "../../repository";
import type { PortfolioLabInvestmentType } from "../../ctx";
import type { InvestmentPickerDialogProvider } from "./InvestmentPickerDialog";
import { spawnInvestmentPickerDialog } from "./spawn-investment-picker-dialog";

type CompositionConfiguration = {
	[K in PortfolioLabInvestmentType]: {
		type: K;
		label: string;
		icon: IconName;
		description?: string;
		spawn(args: { onSubmitAsync(entities: InvestmentPickerDialogProvider[]): Promise<void> }): SpawnResult<void>;
	};
};

function toPickerProvider(item: ItemData): InvestmentPickerDialogProvider[] {
	const uuid = item.predefinedFields?.uuid;
	const name = item.predefinedFields?.name;
	const status = item.predefinedFields?.status;
	if (!uuid || !name || !status) {
		return [];
	}
	return [{ uuid, name, status }];
}

export function getCompositionPickerConfigMap() {
	return {
		INVESTMENT: {
			type: "INVESTMENT",
			label: "Portfolio",
			description: "Load an existing portfolio",
			icon: "Portfolio",
			spawn: ({ onSubmitAsync }) =>
				spawnInvestmentPickerDialog({
					title: "Portfolio",
					multiple: true,
					compositionProvider: async (search) => {
						const response = await portfolioLabRepository.investmentList.fetchQuery(search);
						return (response.items ?? []).flatMap(toPickerProvider);
					},
					onSubmitAsync,
				}),
		},
		INVESTMENT_PROPOSAL: {
			type: "INVESTMENT_PROPOSAL",
			label: "Portfolio Proposal",
			description: "Load a portfolio proposal",
			icon: "Enhance",
			spawn: ({ onSubmitAsync }) =>
				spawnInvestmentPickerDialog({
					title: "Portfolio Proposal",
					multiple: true,
					compositionProvider: async (search) => {
						const response = await portfolioLabRepository.proposalList.fetchQuery(search);
						return (response.items ?? []).flatMap(toPickerProvider);
					},
					onSubmitAsync,
				}),
		},
		BENCHMARK: {
			type: "BENCHMARK",
			label: "Benchmark",
			description: "Load a benchmark",
			icon: "benchmark",
			spawn: ({ onSubmitAsync }) =>
				spawnInvestmentPickerDialog({
					title: "Benchmark",
					multiple: true,
					compositionProvider: async (search) => {
						const response = await portfolioLabRepository.referenceAndBenchmarkList.fetchQuery(search);
						return (response.items ?? [])
							.filter((item) => item.predefinedFields?.investmentType === MandatoryItemDataInvestmentTypeEnum.Benchmark)
							.flatMap(toPickerProvider);
					},
					onSubmitAsync,
				}),
		},
		REFERENCE: {
			type: "REFERENCE",
			label: "Reference",
			description: "Load a reference portfolio",
			icon: "References",
			spawn: ({ onSubmitAsync }) =>
				spawnInvestmentPickerDialog({
					title: "Reference",
					multiple: true,
					compositionProvider: async (search) => {
						const response = await portfolioLabRepository.referenceAndBenchmarkList.fetchQuery(search);
						return (response.items ?? [])
							.filter((item) => item.predefinedFields?.investmentType === MandatoryItemDataInvestmentTypeEnum.Reference)
							.flatMap(toPickerProvider);
					},
					onSubmitAsync,
				}),
		},
	} satisfies CompositionConfiguration;
}

export function getCompositionPickerConfigOptions() {
	return typedObjectValues(getCompositionPickerConfigMap());
}
